import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import {
  BottomNavbarComponent,
  GameDetailsComponent,
  OrAvatarComponent,
  OrIconComponent,
  PageLayoutComponent,
  TitileNavbarComponent
} from '@ui-components';
import { IonicModule } from '@ionic/angular';
import {
  GameDetailsModel,
  GamePlayerModel,
  GlobalRefreshService,
  MODAL_TOKEN,
  ModalProvider,
  provideLocalRefreshService,
  RedirectToUserProfileDirective,
  USER_DATA_TOKEN,
  UserDataProvider,
  UserModel
} from '@core';
import { Observable, shareReplay, switchMap, tap } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  ApproveApplicationsCommandHandler,
  GetHostedGameQueryHandler,
  MarkAsPaidCommandHandler,
  RejectApplicationsCommandHandler
} from '../../application/handlers';
import { ReactiveFormsModule } from '@angular/forms';
import { InviteOptionsComponent } from '../invite-options/invite-options.component';
import { OpenSingleChatCommandHandler } from '@messages';
import { cardReasonMapper, CardReasonModel } from '@fair-play';
import { ChatService } from '@messages/infra';
import { AppPreferredPlayerPosition, PenaltyReason } from '@core-http';
import { HostedGameDetailsViewService } from './view-service/hosted-game-details.view-service';

@Component({
  selector: 'lib-hosted-game-details',
  templateUrl: './hosted-game-details.component.html',
  styleUrls: ['./hosted-game-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    TitileNavbarComponent,
    IonicModule,
    BottomNavbarComponent,
    ReactiveFormsModule,
    OrAvatarComponent,
    GameDetailsComponent,
    InviteOptionsComponent,
    RedirectToUserProfileDirective,
    PageLayoutComponent,
    OrIconComponent
  ],
  providers: [
    MarkAsPaidCommandHandler,
    GetHostedGameQueryHandler,
    ApproveApplicationsCommandHandler,
    RejectApplicationsCommandHandler,
    provideLocalRefreshService(),
    OpenSingleChatCommandHandler,
    ChatService,
    HostedGameDetailsViewService
  ]
})
export class HostedGameDetailsComponent {
  private readonly getHostedGameQueryHandler: GetHostedGameQueryHandler = inject(GetHostedGameQueryHandler);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly globalRefreshService: GlobalRefreshService = inject(GlobalRefreshService);
  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);
  private readonly viewService: HostedGameDetailsViewService = inject(HostedGameDetailsViewService);

  readonly cardReasonMapper: Record<PenaltyReason, CardReasonModel> = cardReasonMapper;
  readonly playersLeftExpanded: WritableSignal<boolean> = signal<boolean>(false);
  readonly invitationsRejectedExpanded: WritableSignal<boolean> = signal<boolean>(false);
  readonly preferredPosition: typeof AppPreferredPlayerPosition = AppPreferredPlayerPosition;

  private readonly game$: Observable<GameDetailsModel> = this.viewService.refresh$.pipe(
    switchMap(() => this.globalRefreshService.refresh$),
    switchMap(() => this.activatedRoute.params),
    switchMap((params: Params) => this.getHostedGameQueryHandler.getGame(params['id'])),
    tap(() => this.modalProvider.dismissLoading$()),
    shareReplay(1)
  );

  readonly game: Signal<GameDetailsModel | undefined> = toSignal(this.game$);

  readonly userData: Signal<UserModel | undefined> = toSignal(this.userDataProvider.userData$);

  readonly isInviteOptionsVisible: Signal<boolean> = computed(() => {
    const game: GameDetailsModel | undefined = this.game();
    const userData: UserModel | undefined = this.userData();
    if (!game || !userData) return false;
    return this.calculateOptionsVisible(game, userData);
  });

  constructor() {
    this.initializeLoading();
  }

  acceptPlayer(gameId: string, gamePlayerApplicationId: string): void {
    this.viewService.acceptPlayer(gameId, gamePlayerApplicationId);
  }

  rejectApplication(gameId: string, gamePlayerApplicationId: string): void {
    this.viewService.rejectApplication(gameId, gamePlayerApplicationId);
  }

  managePlayer(gameId: string, player: GamePlayerModel, isPlayerInGame: boolean): void {
    this.viewService.managePlayer(gameId, player, isPlayerInGame);
  }

  manageGame(): void {
    this.viewService.manageGame(this.game(), this.userData());
  }

  sendMessage(userId: string, displayName: string | undefined): void {
    this.viewService.sendMessage(userId, this.router.url, displayName);
  }

  changePaymentStatus(userId: string, isPaid: boolean): void {
    this.viewService.changePaymentStatus(this.game(), userId, isPaid);
  }

  refresh(): void {
    this.viewService.refresh();
  }

  changePlayersLeftVisibility(): void {
    this.playersLeftExpanded.update((value: boolean) => !value);
  }

  changeInvitationsRejectedVisibility(): void {
    this.invitationsRejectedExpanded.update((value: boolean) => !value);
  }

  summaryInvitations(rejected: number | undefined, active: number | undefined): number {
    return (rejected || 0) + (active || 0) || 0;
  }

  private initializeLoading(): void {
    this.viewService.initializeLoading();
  }

  private calculateOptionsVisible(game: GameDetailsModel, userData: UserModel): boolean {
    const isHostInGame: boolean =
      game?.players?.some((player: GamePlayerModel) => player.userId === userData.userId) || false;

    const playersInGame: number | undefined = isHostInGame ? (game.players?.length || 0) - 1 : game.players?.length;

    return !playersInGame;
  }
}
